import { Link } from "react-router-dom";
import { Header, Footer, PageHero } from "../components/SharedComponents";
import {
  FaCheck, FaCalculator, FaShieldAlt, FaDollarSign, FaGlobe,
  FaArrowRight, FaChartLine
} from "react-icons/fa";

const serviceIncludes = [
  ["Accurate Monthly Bookkeeping", "Bank & Account Reconciliation",
    "Accounts Payable & Receivable", "Payroll Integration",
    "Foreign Entity Setup and Compliance"],
  ["Financial Statement Preparation", "Cash Flow Tracking & Reporting",
    "Tax-Ready Books & Records", "Cloud-Based Bookkeeping",
    "Year-End Reporting & Support"]
];

const benefits = [
  { icon: <FaCalculator size={32} />, title: "Expertise and Knowledge", blue: true,
    desc: "Our certified bookkeeping professionals bring years of hands-on financial expertise to every client engagement." },
  { icon: <FaShieldAlt size={32} />, title: "Compliance and Risk Management", blue: false,
    desc: "Stay fully compliant with tax laws and financial regulations, reducing risk and avoiding costly penalties." },
  { icon: <FaDollarSign size={32} />, title: "Cost Savings and Efficiency", blue: false,
    desc: "Outsourced bookkeeping eliminates overhead costs while delivering precision and speed you can count on." },
  { icon: <FaGlobe size={32} />, title: "Global Reach and Local Expertise", blue: false,
    desc: "We serve businesses locally and internationally, adapting to your specific industry and geographic needs." },
];

const processSteps = [
  {
    num: "01",
    title: "Consultation and Assessment",
    desc: "We begin with a thorough consultation to understand your business structure and current bookkeeping challenges.",
    points: [
      "Schedule a meeting with our experts to discuss your financial challenges and goals.",
      "We conduct a thorough assessment of your current financial situation."
    ],
    dark: true
  },
  {
    num: "02",
    title: "Customized Strategy Development",
    desc: "Our team creates a tailored bookkeeping plan and strategy designed to address your specific needs.",
    points: [
      "Our team creates a tailored financial plan and strategy designed to address your specific needs.",
      "We outline clear steps and solutions to achieve your financial objectives."
    ],
    dark: false
  },
  {
    num: "03",
    title: "Implementation and Support",
    desc: "We implement the agreed-upon strategies, providing continuous monitoring and adjustments as needed.",
    points: [
      "We implement the agreed-upon strategies, providing continuous monitoring and adjustments as needed.",
      "Ongoing support & regular updates ensure you stay on financial success."
    ],
    dark: false
  }
];

const BookkeepingPage = () => (
  <div className="site-wrapper">
    <Header />
    <PageHero
      title="Bookkeeping"
      bg="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80"
    />

    {/* Main 2-column layout */}
    <section className="svc-page-body">
      <div className="container">
        <div className="svc-page-grid">

          {/* LEFT SIDEBAR */}
          <aside className="svc-sidebar">
            <div className="svc-trouble-card">
              <h3>Having Trouble Evaluating Your Finances?</h3>
              <p>Let United Taxpert make it simple. Our financial experts help you assess your current position, uncover inefficiencies, and create a clear path toward better financial health. From tax analysis to cash flow management, we provide the clarity and strategy you need to make confident financial decisions.</p>
              <a href="mailto:refunds@unitedtaxpert.com" className="btn btn-primary btn-full">Contact Us Now</a>
            </div>
            <div className="svc-sidebar-img">
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&auto=format&fit=crop&q=80"
                alt="Financial advisor"
              />
            </div>
          </aside>

          {/* RIGHT MAIN CONTENT */}
          <div className="svc-main-content">
            <h2>Accurate Bookkeeping &amp; Accounting Services</h2>
            <p className="svc-intro">At United Taxpert, we provide comprehensive bookkeeping services that keep your business finances organized, accurate, and tax-ready. Our expert team handles everything from daily transaction recording to monthly reconciliations and year-end financial reporting, giving you the clarity you need to make informed business decisions and stay compliant with all regulatory requirements.</p>

            <div className="svc-content-img">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80"
                alt="Bookkeeping dashboard"
              />
            </div>

            {/* Service Include */}
            <div className="svc-include">
              <h3>Service Include</h3>
              <p>Our bookkeeping services are designed to give your business a complete financial management solution. We handle all aspects of your books so you can focus on what you do best — running your business.</p>
              <div className="svc-include-grid">
                {serviceIncludes.map((col, ci) => (
                  <ul key={ci} className="svc-include-list">
                    {col.map((item, i) => (
                      <li key={i}><FaCheck className="svc-check" /> {item}</li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="svc-benefits">
              <h3>Benefits</h3>
              <p>When you partner with United Taxpert for bookkeeping, you gain more than just organized records. You gain a financial partner dedicated to your growth, compliance, and long-term success.</p>
              <div className="svc-benefits-grid">
                {benefits.map((b, i) => (
                  <div key={i} className={`svc-benefit-card ${b.blue ? "blue" : "white"}`}>
                    <div className="svc-benefit-icon">{b.icon}</div>
                    <h4>{b.title}</h4>
                    <p>{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing CTA */}
            <div className="svc-pricing-cta">
              <div className="pricing-cta-text">
                <FaChartLine size={24} />
                <div>
                  <h4>View Our Pricing Plans</h4>
                  <p>Transparent, competitive pricing for every business size.</p>
                </div>
              </div>
              <Link to="/pricing" className="btn btn-primary">
                See Pricing Plans <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Process Steps */}
    <section className="svc-process-section">
      <div className="svc-process-overlay" />
      <div className="container">
        <div className="svc-process-grid">
          {processSteps.map((step, i) => (
            <div key={i} className={`svc-step-card ${step.dark ? "dark" : "light"}`}>
              <div className="svc-step-num">{step.num}</div>
              <h4>{step.title}</h4>
              <p>{step.desc}</p>
              <ul>
                {step.points.map((pt, pi) => (
                  <li key={pi}><span className="step-bullet">&#8212;</span> {pt}</li>
                ))}
              </ul>
            </div>
          ))}
          <div className="svc-precision-card">
            <h3>Precision in Financial <span className="text-blue">*Steps</span></h3>
            <p>We follow a proven, structured process to ensure your bookkeeping is handled with the highest level of accuracy, compliance, and strategic insight at every stage.</p>
            <a href="/#contact" className="btn btn-primary">Consultation Now</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
);

export default BookkeepingPage;
